<template>
    <div
        class="w-full gap-2 md:gap-8 flex flex-wrap items-center justify-start gap-y-2 md:justify-start"
        v-if="!loading"
    >
        <UiMultiSelect
            label="Terminal"
            :showCounter="true"
            :options="availableTerminals"
            v-model:modelValue="localFilters.terminals"
            labelKey="value"
            valueKey="value"
            :disabled="!availableTerminals.length"
            @changed="
                () => setSingleFilter('terminals', localFilters.terminals)
            "
            class="order-1 md:block"
            :class="{ hidden: !showMap }"
        />
        <UiMultiSelect
            label="Amenities"
            :showCounter="true"
            :options="availableAmenities"
            v-model:modelValue="localFilters.amenities"
            labelKey="label"
            valueKey="value"
            :disabled="!availableAmenities.length"
            @changed="
                () => setSingleFilter('amenities', localFilters.amenities)
            "
            class="order-2 md:block"
            :class="{ hidden: !showMap }"
        />
        <UiSingleSelect
            label="Open"
            :options="openOptions"
            v-model="localOpenStatusFilter"
            labelKey="label"
            valueKey="value"
            :showCounter="true"
            @update:modelValue="handleChangeOpenStatusFilter"
            class="order-3 md:block"
            :class="{ hidden: !showMap }"
        />
        <UiAutosuggestFlexible
            :preloadData="true"
            :preloadedOptions="availableAirlines"
            mode="multiple"
            label="Airline"
            placeholder="Airline"
            :showCounter="true"
            :selectedOptions="localFilters.airlines"
            valueKey="value"
            labelKey="name"
            displayKey="name"
            :disabled="!availableAirlines.length"
            @toggleSelect="toggleSelectAirline"
            class="order-4 md:block"
            :class="{ hidden: !showMap }"
        />

        <!-- All filters modal button: -->
        <button
            class="btn-base btn-hollow order-5 font-medium whitespace-nowrap rounded shadow dark:shadow-slate-900/50 border dark:border-slate-600 inline-flex items-center gap-x-1.5 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 dark:text-slate-200 hover:border-slate-800 dark:hover:border-slate-500 focus:outline focus:border-slate-800 focus:outline-none disabled:opacity-50 disabled:text-slate-800 dark:text-slate-100 disabled:bg-white dark:disabled:bg-slate-800 disabled:border-slate-300 dark:border-slate-600 max-md:flex-1 max-md:justify-center"
            type="button"
            @click.prevent="() => setShowLoungeFilterModal(true)"
            v-if="!showMap"
            id="hs-lounge-all-filters-btn"
        >
            <div class="w-5 flex items-center justify-center">
                <Icon icon="filters" />
            </div>
            <div
                class="text-slate-800 dark:text-slate-100 dark:text-slate-200 text-sm font-medium leading-tight @2xl:block hidden"
            >
                All filters
            </div>
            <div
                class="text-slate-800 dark:text-slate-100 dark:text-slate-200 text-sm font-medium leading-tight block @2xl:hidden"
            >
                Filters
            </div>
            <div
                class="px-1.5 py-0.5 bg-slate-800 rounded-[999px] flex items-center justify-center text-white text-xs font-medium leading-none w-4"
                :class="{
                    hidden: numFiltersApplied === 0,
                }"
            >
                {{ numFiltersApplied }}
            </div>
        </button>
        <button
            class="btn-base btn-hollow order-6 font-medium whitespace-nowrap rounded shadow dark:shadow-slate-900/50 border dark:border-slate-600 items-center gap-x-1.5 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 dark:text-slate-200 hover:border-slate-800 dark:hover:border-slate-500 focus:outline focus:border-slate-800 focus:outline-none disabled:opacity-50 disabled:text-slate-800 dark:text-slate-100 disabled:bg-white dark:disabled:bg-slate-800 disabled:border-slate-300 dark:border-slate-600 max-md:flex-1 max-md:justify-center hidden max-md:inline-flex"
            type="button"
            @click.prevent="$emit('toggleMap')"
            v-if="!showMap"
            id="hs-lounge-map-toggle-btn"
        >
            <div class="w-5 h-5 flex items-center justify-center">
                <Icon icon="map" />
            </div>
            <div
                class="text-slate-800 dark:text-slate-100 dark:text-slate-200 text-sm font-medium leading-tight hidden sm:block"
            >
                Map
            </div>
        </button>
    </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import _ from 'lodash';
import { storeToRefs } from 'pinia';
import { useLoungesStore } from '@/store/lounges';
import FavButton from '@/components/ui/fav-button/fav-button.vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiAutosuggestFlexible from '@/components/ui/inputs/autosuggest-flexible/ui-autosuggest-flexible.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import UiMultiSelect from '@/components/ui/inputs/multi-select/ui-multi-select.vue';
import UiSingleSelect from '@/components/ui/inputs/single-select/ui-single-select.vue';

export default {
    emits: ['toggleMap'],
    components: {
        FavButton,
        Icon,
        UiAutosuggestFlexible,
        UiMultiSelect,
        UiInput,
        UiSingleSelect,
    },

    setup() {
        const loungesStore = useLoungesStore();
        const {
            availableAmenities,
            availableTerminals,
            searchResultsCount,
            filters,
            openOptions,
            availableAirlines,
            loading,
            showMap,
        } = storeToRefs(loungesStore);
        const { setSingleFilter, setShowLoungeFilterModal } = loungesStore;

        const localFilters = ref({
            terminals: [],
            amenities: [],
            airlines: [],
        });
        const localOpenStatusFilter = ref({});

        const numFiltersApplied = computed(() => {
            return Object.values(filters.value).reduce((count, arr) => {
                return (count += arr.length);
            }, 0);
        });

        const handleChangeOpenStatusFilter = () => {
            const val = _.isEmpty(localOpenStatusFilter.value)
                ? []
                : [localOpenStatusFilter.value];

            setSingleFilter('openStatus', val);
        };

        const toggleSelectAirline = (al) => {
            const isSelected = localFilters.value.airlines.some(
                (airline) => airline.value === al.value
            );

            if (isSelected) {
                localFilters.value.airlines =
                    localFilters.value.airlines.filter(
                        (item) => item.value !== al.value
                    );
            } else {
                localFilters.value.airlines = [
                    ...localFilters.value.airlines,
                    al,
                ];
            }

            // Apply the filter to the store
            setSingleFilter('airlines', localFilters.value.airlines);
        };

        watch(
            filters,
            () => {
                Object.keys(localFilters.value).forEach((key) => {
                    localFilters.value[key] = filters.value[key] || [];
                });

                const open = filters.value.openStatus || [];
                localOpenStatusFilter.value = open.length ? open[0] : {};
            },
            { deep: true, immediate: true }
        );

        return {
            availableAmenities,
            availableTerminals,
            handleChangeOpenStatusFilter,
            searchResultsCount,
            loading,
            localFilters,
            localOpenStatusFilter,
            numFiltersApplied,
            openOptions,
            setShowLoungeFilterModal,
            setSingleFilter,
            availableAirlines,
            showMap,
            toggleSelectAirline,
        };
    },
};
</script>
